/**
 * Create-secret dialog. Two modes:
 *
 *  - **paste**: operator supplies the plaintext directly. For existing keys
 *    or pre-issued tokens (OAuth client secrets, API tokens, etc).
 *
 *  - **generate-ssh-ed25519**: server generates the keypair. The private key
 *    is encrypted and stored; the public key + fingerprint come back so the
 *    operator can copy them into `authorized_keys` on the target server.
 *
 * The UX has two phases:
 *
 *  1. Form. Pick mode, fill it in, submit.
 *  2. Reveal. We never see the plaintext again, so on success we show a
 *     read-only panel with copy buttons and an explicit "I have saved this"
 *     dismiss step. The dialog will not close on its own.
 *
 * For paste mode the second phase echoes the plaintext (operator already had
 * it — this is for parity with the generate flow). For generate mode it
 * shows the public key prominently and the private key in a collapsible
 * panel that defaults closed (since most operators only need the public).
 */
import { useState, type FormEvent } from 'react';
import {
  KeyRound,
  Loader2,
  Copy,
  Eye,
  EyeOff,
  CheckCircle2,
  AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog.js';
import { Button } from '@/components/ui/Button.js';
import { Input } from '@/components/ui/Input.js';
import { Label } from '@/components/ui/Label.js';
import { Textarea } from '@/components/ui/Textarea.js';
import {
  api,
  ApiError,
  type CreatedSecret,
  type CreateSecretInput,
  type PrincipalResponse,
} from '@/api/client.js';
import { cn } from '@/lib/utils.js';

type Membership = PrincipalResponse['memberships'][number];

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  memberships: Membership[];
  /** When set, the dialog opens with this org pre-selected and unchangeable. */
  lockedOrgId?: string | undefined;
  onCreated: (created: CreatedSecret) => void | Promise<void>;
}

type Mode = 'paste' | 'generate-ssh-ed25519';

export function CreateSecretDialog({
  open,
  onOpenChange,
  memberships,
  lockedOrgId,
  onCreated,
}: Props): JSX.Element {
  const [orgId, setOrgId] = useState(lockedOrgId ?? memberships[0]?.orgId ?? '');
  const [mode, setMode] = useState<Mode>('generate-ssh-ed25519');
  const [name, setName] = useState('');
  const [plaintext, setPlaintext] = useState('');
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<CreatedSecret | null>(null);

  function reset() {
    setOrgId(lockedOrgId ?? memberships[0]?.orgId ?? '');
    setMode('generate-ssh-ed25519');
    setName('');
    setPlaintext('');
    setComment('');
    setError(null);
    setSubmitting(false);
    setCreated(null);
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!orgId) {
      setError('Pick an organisation.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const input: CreateSecretInput =
        mode === 'paste'
          ? { mode: 'paste', name, plaintext }
          : { mode: 'generate-ssh-ed25519', name, ...(comment ? { comment } : {}) };
      const result = await api.secrets.create(orgId, input);
      setCreated(result);
      await onCreated(result);
      toast.success(`Created secret "${result.name}"`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not create secret.');
    } finally {
      setSubmitting(false);
    }
  }

  function close() {
    onOpenChange(false);
    reset();
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent>
        {created ? (
          <RevealPhase created={created} onDismiss={close} />
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <DialogHeader>
              <DialogTitle>New secret</DialogTitle>
              <DialogDescription>
                Encrypted at rest with AES-256-GCM. The plaintext is returned exactly once at
                creation; after that, only the metadata is visible to anyone.
              </DialogDescription>
            </DialogHeader>

            {!lockedOrgId && (
              <div className="space-y-1.5">
                <Label htmlFor="org">Organisation</Label>
                <select
                  id="org"
                  value={orgId}
                  onChange={(e) => setOrgId(e.target.value)}
                  className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {memberships.map((m) => (
                    <option key={m.orgId} value={m.orgId}>
                      {m.orgName} (/{m.orgSlug})
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="space-y-1.5">
              <Label>Mode</Label>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <ModeButton
                  selected={mode === 'generate-ssh-ed25519'}
                  onSelect={() => setMode('generate-ssh-ed25519')}
                  title="Generate SSH keypair"
                  hint="Server creates an ed25519 keypair. We keep the private key encrypted; you copy the public key to the server."
                />
                <ModeButton
                  selected={mode === 'paste'}
                  onSelect={() => setMode('paste')}
                  title="Paste existing"
                  hint="For pre-existing keys, OAuth client secrets, API tokens, anything else."
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                required
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={mode === 'paste' ? 'github-deploy-token' : 'web-01-ssh-key'}
              />
              <p className="text-[11px] text-muted-foreground">
                Unique within the organisation. Used to identify the secret in audit and pickers.
              </p>
            </div>

            {mode === 'paste' ? (
              <div className="space-y-1.5">
                <Label htmlFor="plaintext">Plaintext</Label>
                <Textarea
                  id="plaintext"
                  required
                  rows={6}
                  value={plaintext}
                  onChange={(e) => setPlaintext(e.target.value)}
                  className="font-mono text-xs"
                  placeholder={`-----BEGIN OPENSSH PRIVATE KEY-----\n…\n-----END OPENSSH PRIVATE KEY-----`}
                />
                <p className="text-[11px] text-muted-foreground">
                  Up to 64 KB. Encrypted with AES-256-GCM before write.
                </p>
              </div>
            ) : (
              <div className="space-y-1.5">
                <Label htmlFor="comment">Public-key comment</Label>
                <Input
                  id="comment"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder={`deploy@${name || 'host'} (optional)`}
                />
                <p className="text-[11px] text-muted-foreground">
                  Goes into the OpenSSH public key's trailing comment. Defaults to the secret name.
                </p>
              </div>
            )}

            {error && (
              <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {error}
              </p>
            )}

            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting || !name || (mode === 'paste' && !plaintext)}
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
                {mode === 'paste' ? 'Store secret' : 'Generate keypair'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ModeButton({
  selected,
  onSelect,
  title,
  hint,
}: {
  selected: boolean;
  onSelect: () => void;
  title: string;
  hint: string;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'flex flex-col items-start gap-1 rounded-md border px-3 py-2 text-left transition-colors',
        selected ? 'border-primary bg-primary/10' : 'border-border bg-background hover:bg-accent/40',
      )}
    >
      <span className="text-sm font-medium">{title}</span>
      <span className="text-[11px] leading-snug text-muted-foreground">{hint}</span>
    </button>
  );
}

/* ------------------------------------------------------------------------- *
 * Reveal phase — shown after a successful create.                            *
 * ------------------------------------------------------------------------- */

function RevealPhase({ created, onDismiss }: { created: CreatedSecret; onDismiss: () => void }): JSX.Element {
  const isSshKey = !!created.publicKey;
  return (
    <div className="space-y-4">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <CheckCircle2 className="h-5 w-5 text-emerald-500" />
          Secret created
        </DialogTitle>
        <DialogDescription>
          {isSshKey
            ? 'Copy the public key to the target server (`authorized_keys`). The private key stays encrypted server-side — DeployIt will never show it again.'
            : 'This is the only time the plaintext is shown. Copy it now if you need to retain it; otherwise DeployIt keeps it encrypted at rest.'}
        </DialogDescription>
      </DialogHeader>

      <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1.5 text-xs">
        <dt className="text-muted-foreground">Name</dt>
        <dd className="font-medium">{created.name}</dd>
        <dt className="text-muted-foreground">ID</dt>
        <dd className="break-all font-mono">{created.id}</dd>
        {created.fingerprint && (
          <>
            <dt className="text-muted-foreground">Fingerprint</dt>
            <dd className="break-all font-mono">{created.fingerprint}</dd>
          </>
        )}
      </dl>

      {created.publicKey && (
        <CopyBlock label="Public key (paste into authorized_keys)" value={created.publicKey} />
      )}

      <PrivatePlaintextBlock
        label={isSshKey ? 'Private key (encrypted; this is your one chance to save a copy)' : 'Plaintext'}
        value={created.plaintext}
      />

      <DialogFooter>
        <Button onClick={onDismiss}>I've saved what I need</Button>
      </DialogFooter>
    </div>
  );
}

function CopyBlock({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <Label>{label}</Label>
        <Button
          type="button"
          variant="ghost"
          onClick={() => {
            void navigator.clipboard.writeText(value);
            toast.success('Copied');
          }}
        >
          <Copy className="h-3.5 w-3.5" />
          Copy
        </Button>
      </div>
      <pre className="overflow-x-auto break-all rounded-md border border-border bg-muted/30 p-3 font-mono text-[11px]">
        {value}
      </pre>
    </div>
  );
}

function PrivatePlaintextBlock({ label, value }: { label: string; value: string }): JSX.Element {
  const [visible, setVisible] = useState(false);
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <Label className="flex items-center gap-1.5 text-amber-700 dark:text-amber-300">
          <AlertTriangle className="h-3.5 w-3.5" />
          {label}
        </Label>
        <div className="flex items-center gap-1">
          <Button type="button" variant="ghost" onClick={() => setVisible((v) => !v)}>
            {visible ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
            {visible ? 'Hide' : 'Show'}
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              void navigator.clipboard.writeText(value);
              toast.success('Copied');
            }}
          >
            <Copy className="h-3.5 w-3.5" />
            Copy
          </Button>
        </div>
      </div>
      <pre className="overflow-x-auto break-all rounded-md border border-amber-500/40 bg-amber-500/5 p-3 font-mono text-[11px]">
        {visible ? value : '•'.repeat(Math.min(value.length, 80))}
      </pre>
    </div>
  );
}
